import { ChatMessage, ChatSocketClient } from './socket-client';
import { WIDGET_CSS } from './styles';
import { Lang, Strings, resolveLang, strings } from './i18n';
import { BookingCard, ServiceOption, calendarLink, dayLabel, escapeHtml, fullSlotLabel, groupSlots, parseMessage, renderRichText, timeLabel, todayIn } from './markup';

type ContactChannel = 'whatsapp' | 'sms' | 'call' | 'email';
const CONTACT_CHANNELS: ContactChannel[] = ['whatsapp', 'sms', 'call', 'email'];

interface WidgetSettings {
  position: 'right' | 'left';
  launcherLabel: string;
  showLauncherLabel: boolean;
  subtitle: string;
  language: 'auto' | 'es' | 'en';
  showBranding: boolean;
  contacts?: Partial<Record<ContactChannel, { enabled?: boolean; value?: string }>>;
  contactColors?: 'brand' | 'accent';
}

interface WidgetConfig {
  tenantId?: string;
  siteUrl?: string; // LeadBridge public site, for the "Powered by" link
  businessName: string;
  botName: string;
  logoUrl?: string | null;
  botLogoUrl?: string | null;
  accentColor?: string;
  quickReplies?: string[];
  chatDesignTheme?: string;
  leadCaptureMode?: string;
  welcomeTitle?: string;
  welcomeDesc?: string;
  initialMessage?: string;
  widgetSettings?: Partial<WidgetSettings>;
  booking?: { enabled: boolean; timezone: string };
}

type Field = 'name' | 'email' | 'phone';
interface PendingMessage { content: string; element: HTMLElement; meta?: Record<string, string> }
const DELIVERY_TIMEOUT_MS = 12_000;
interface Session { chatId: string; leadId: string }

const ICONS = {
  chat: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a8.5 8.5 0 0 1-12.4 7.6L3 21l1.4-5.6A8.5 8.5 0 1 1 21 12z"/></svg>',
  down: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>',
  close: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>',
  more: '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>',
  send: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>',
  check: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m8 12 3 3 5-6"/></svg>',
  clock: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
  plus: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
  tick: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 5 5 9-10"/></svg>',
  whatsapp: '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>',
  sms: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M8 9h8M8 13h5"/></svg>',
  call: '<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>',
  email: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>'
};

/** Link a contact button opens: the visitor's own WhatsApp, SMS app, dialer or email app. */
function contactHref(channel: ContactChannel, value: string): string | null {
  const text = value.trim();
  if (channel === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text) ? `mailto:${text}` : null;
  const digits = text.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) return null;
  if (channel === 'whatsapp') return `https://wa.me/${digits}`;
  const number = text.startsWith('+') ? `+${digits}` : digits;
  return channel === 'sms' ? `sms:${number}` : `tel:${number}`;
}

const DEFAULT_SETTINGS: WidgetSettings = {
  position: 'right', launcherLabel: '', showLauncherLabel: true, subtitle: '', language: 'auto', showBranding: true, contacts: {}, contactColors: 'brand'
};
const DARK_THEMES = ['dark', 'dark-booking', 'high-end'];

/** Readable text color (dark or white) for any accent color. */
function onAccent(hex: string): string {
  const value = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map(i => parseInt(value.slice(i, i + 2), 16) / 255).map(c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.45 ? '#0f172a' : '#ffffff';
}

/**
 * True when an element makes position: fixed children follow the page instead of the screen
 * (transform, filter, containment…). Some themes do this to <body> on large screens only.
 */
function trapsFixed(element: Element): boolean {
  const style = getComputedStyle(element);
  return style.transform !== 'none' || style.perspective !== 'none' || style.filter !== 'none'
    || (style.backdropFilter || 'none') !== 'none'
    || /paint|layout|strict|content/.test(style.contain || '')
    || /transform|perspective|filter/.test(style.willChange || '')
    || (style.containerType || 'normal') !== 'normal';
}

const formatTime = (iso: string, lang: Lang) => new Date(iso).toLocaleTimeString(lang === 'en' ? 'en-US' : 'es-PE', { hour: 'numeric', minute: '2-digit' });

class LeadBridgeWidget {
  private token = '';
  private tenantId = '';
  private apiUrl = 'http://localhost:3001';
  private preview = false;
  private previewLive = false; // dashboard "test the bot" mode: the preview talks to the real AI
  private config: WidgetConfig = { businessName: '', botName: 'Asistente', initialMessage: '¡Hola! ¿En qué podemos ayudarte?' };
  private settings: WidgetSettings = DEFAULT_SETTINGS;
  private lang: Lang = 'es';
  private t: Strings = strings('es');

  private host!: HTMLElement;
  private root!: HTMLElement;
  private body!: HTMLElement;
  private suggestions!: HTMLElement;
  private composer!: HTMLFormElement;
  private input!: HTMLTextAreaElement;
  private sendButton!: HTMLButtonElement;

  private isOpen = false;
  private view: 'welcome' | 'chat' = 'welcome';
  private session: Session | null = null;
  private socket: ChatSocketClient | null = null;
  private rendered = new Set<string>();
  private pending: PendingMessage[] = [];
  private lastSender = '';
  private lastTime = 0;
  private unread = 0;
  private openedOnce = false;
  private labelDismissed = false;
  private labelKey = '';
  private chatClosed = false;
  private typingTimer: ReturnType<typeof setTimeout> | null = null;
  private visitorHasWritten = false;
  private savedOverflow: string | null = null; // host page overflow while the phone chat is open

  constructor(identifier: string, isToken = false, apiUrl = '', preview = false) {
    if (apiUrl) this.apiUrl = apiUrl;
    if (isToken) this.token = identifier; else this.tenantId = identifier;
    this.preview = preview;
    this.previewLive = preview && document.querySelector('script[data-preview-live="true"]') !== null;
    if (document.readyState === 'loading' || !document.body) document.addEventListener('DOMContentLoaded', () => this.init());
    else this.init();
  }

  // ---------- Setup ----------

  private async init() {
    this.mount();
    const enabled = await this.fetchConfig();
    if (!enabled) { this.host.remove(); return; }
    if (this.preview) {
      this.listenForPreviewConfig();
      this.setOpen(true);
    }
    await this.restoreSession();
  }

  private mount() {
    this.host = document.createElement('div');
    this.host.id = 'leadbridge-widget-container';
    const shadow = this.host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = WIDGET_CSS;
    this.root = document.createElement('div');
    this.root.className = 'lb-root';
    shadow.append(style, this.root);
    this.placeHost();
    // Themes often add those styles later (page animations, smooth scrolling) or per screen size.
    let frame = 0;
    const recheck = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => this.placeHost()); };
    window.addEventListener('load', recheck);
    window.addEventListener('resize', recheck);
    new MutationObserver(recheck).observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
    this.renderFrame();
    this.applyConfig();

    const mobile = window.matchMedia('(max-width: 520px)');
    const syncMobile = () => this.root.classList.toggle('lb-mobile', mobile.matches && !this.preview);
    syncMobile();
    mobile.addEventListener?.('change', syncMobile);
    this.root.addEventListener('keydown', event => { if (event.key === 'Escape' && this.isOpen && !this.preview) this.setOpen(false); });
  }

  /**
   * Keeps the chat pinned to the screen: under <body> normally, under <html> when <body> would make it
   * scroll with the page (it then sits below the footer, out of sight, e.g. on desktop only).
   */
  private placeHost() {
    if (!this.host.isConnected && this.host.dataset.mounted) return; // removed (plan inactive)
    const parent = trapsFixed(document.body) ? document.documentElement : document.body;
    if (this.host.parentElement !== parent) parent.appendChild(this.host);
    this.host.dataset.mounted = '1';
  }

  private async fetchConfig(): Promise<boolean> {
    const endpoint = this.token ? `${this.apiUrl}/api/widget/token/${this.token}` : `${this.apiUrl}/api/widget/config/${this.tenantId}`;
    try {
      const res = await fetch(endpoint);
      if (res.status === 402) {
        const body = await res.json().catch(() => ({}));
        console.warn(body.websiteLimitReached
          ? `[LeadBridge] This chatbot is hidden: the plan includes ${body.maxWebsites ?? 'fewer'} website(s) and this is an extra one. Upgrade the plan or remove another chatbot in LeadBridge.`
          : '[LeadBridge] Subscription inactive for this account. The chat widget is disabled.');
        return false;
      }
      if (res.ok) {
        this.config = { ...this.config, ...(await res.json()) };
        if (this.token && this.config.tenantId) this.tenantId = this.config.tenantId;
        if (this.preview) this.applyPreviewAttributes();
        this.applyConfig();
      } else {
        console.warn(`[LeadBridge] Could not load configuration (status ${res.status}).`);
      }
    } catch (error) {
      console.error('[LeadBridge] Error loading widget config:', error);
    }
    return true;
  }

  /** Overrides used by the dashboard preview (older templates page). */
  private applyPreviewAttributes() {
    const script = document.querySelector('script[data-preview-mode="true"]');
    if (!script) return;
    const read = (name: string) => script.getAttribute(name);
    if (read('data-preview-theme') !== null) this.config.chatDesignTheme = read('data-preview-theme')!;
    if (read('data-preview-lead-mode') !== null) this.config.leadCaptureMode = read('data-preview-lead-mode')!;
    if (read('data-preview-welcome-title') !== null) this.config.welcomeTitle = read('data-preview-welcome-title')!;
    if (read('data-preview-welcome-desc') !== null) this.config.welcomeDesc = read('data-preview-welcome-desc')!;
    if (read('data-preview-initial-message') !== null) this.config.initialMessage = read('data-preview-initial-message')!;
  }

  /** The dashboard settings page pushes unsaved changes into the preview in real time. */
  private listenForPreviewConfig() {
    window.addEventListener('message', event => {
      if (event.source !== window.parent) return;
      // Editing the contact buttons closes the preview chat so the buttons can be seen.
      if (event.data?.type === 'leadbridge:open' && typeof event.data.open === 'boolean') { if (event.data.open !== this.isOpen) this.setOpen(event.data.open); return; }
      if (event.data?.type !== 'leadbridge:config') return;
      this.config = { ...this.config, ...event.data.config, widgetSettings: { ...this.config.widgetSettings, ...event.data.config?.widgetSettings } };
      this.applyConfig();
      if (!this.session) this.showStart();
    });
    // Ask the dashboard for its unsaved settings now that the listener exists.
    window.parent?.postMessage({ type: 'leadbridge:ready' }, '*');
  }

  private applyConfig() {
    this.settings = { ...DEFAULT_SETTINGS, ...(this.config.widgetSettings || {}) };
    if (String(this.config.leadCaptureMode || '').includes('nopill') && this.config.widgetSettings?.showLauncherLabel === undefined) this.settings.showLauncherLabel = false;
    this.lang = resolveLang(this.settings.language);
    this.t = strings(this.lang);

    const accent = /^#[0-9a-fA-F]{6}$/.test(this.config.accentColor || '') ? this.config.accentColor! : '#6D28D9';
    this.root.style.setProperty('--lb-accent', accent);
    this.root.style.setProperty('--lb-on-accent', onAccent(accent));
    const theme = this.config.chatDesignTheme || 'light';
    const dark = DARK_THEMES.includes(theme) || (theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    this.root.dataset.theme = dark ? 'dark' : 'light';
    this.root.dataset.position = this.settings.position;
    this.root.classList.toggle('lb-preview', this.preview);

    const name = this.displayName();
    this.root.querySelector('.lb-title strong')!.textContent = name;
    this.root.querySelector('.lb-subtitle')!.textContent = this.settings.subtitle || this.t.replies;
    this.root.querySelector('.lb-panel')!.setAttribute('aria-label', name);
    this.renderAvatar(this.root.querySelector('.lb-header .lb-avatar') as HTMLElement);
    (this.root.querySelector('.lb-branding') as HTMLElement).hidden = !this.settings.showBranding;
    (this.root.querySelector('.lb-branding span') as HTMLElement).textContent = this.t.poweredBy;
    const brandLink = this.root.querySelector('.lb-branding a') as HTMLAnchorElement;
    if (/^https:\/\//.test(this.config.siteUrl || '')) brandLink.href = this.config.siteUrl!; else brandLink.removeAttribute('href');
    this.input.placeholder = this.t.placeholder;
    this.sendButton.setAttribute('aria-label', this.t.send);
    this.renderContacts();
    this.updateLauncher();
  }

  /** WhatsApp / SMS / call / email buttons stacked above the chat button (same corner, same spacing). */
  private renderContacts() {
    const nav = this.root.querySelector('.lb-contacts') as HTMLElement;
    nav.replaceChildren();
    this.root.dataset.contacts = this.settings.contactColors === 'accent' ? 'accent' : 'brand';
    for (const channel of CONTACT_CHANNELS) {
      const contact = this.settings.contacts?.[channel];
      const href = contact?.enabled && contact.value ? contactHref(channel, contact.value) : null;
      if (!href) continue;
      const label = this.t.contact[channel];
      const link = document.createElement('a');
      link.className = 'lb-contact';
      link.dataset.channel = channel;
      link.href = href;
      if (channel === 'whatsapp') { link.target = '_blank'; link.rel = 'noopener noreferrer'; }
      link.setAttribute('aria-label', `${label}: ${contact!.value}`);
      link.innerHTML = `${ICONS[channel]}<span class="lb-contact-tip" aria-hidden="true"></span>`;
      (link.querySelector('.lb-contact-tip') as HTMLElement).textContent = channel === 'whatsapp' ? label : `${label} · ${contact!.value}`;
      nav.appendChild(link);
    }
    nav.hidden = !nav.children.length;
    nav.setAttribute('aria-label', this.t.contactUs);
  }

  private displayName() {
    return this.config.botName || `${this.config.businessName || 'Asistente'}`;
  }

  private logo(): string | null {
    const logo = this.config.botLogoUrl || this.config.logoUrl;
    return logo && /^https:\/\//i.test(logo) ? logo : null;
  }

  private renderAvatar(target: HTMLElement) {
    target.replaceChildren();
    const logo = this.logo();
    const initial = this.displayName().charAt(0).toUpperCase();
    if (logo) {
      const img = document.createElement('img');
      img.src = logo; img.alt = '';
      img.onerror = () => { target.textContent = initial; };
      target.appendChild(img);
    } else target.textContent = initial;
  }

  // ---------- Frame ----------

  private renderFrame() {
    this.root.innerHTML = `
      <section class="lb-panel" role="dialog" aria-modal="false">
        <header class="lb-header">
          <div class="lb-avatar"></div>
          <div class="lb-title"><strong></strong><span><i class="lb-dot"></i><span class="lb-subtitle"></span></span></div>
          <button type="button" class="lb-icon-btn" data-action="menu" aria-haspopup="true">${ICONS.more}</button>
          <button type="button" class="lb-icon-btn" data-action="close">${ICONS.down}</button>
          <div class="lb-menu" hidden><button type="button" data-action="new-chat"></button></div>
        </header>
        <div class="lb-body"></div>
        <div class="lb-suggestions" hidden></div>
        <form class="lb-composer" hidden>
          <textarea rows="1" aria-label="Mensaje"></textarea>
          <button type="submit" class="lb-send" disabled>${ICONS.send}</button>
        </form>
        <div class="lb-branding"><span></span> <a target="_blank" rel="noopener">LeadBridge</a></div>
      </section>
      <div class="lb-dock">
        <nav class="lb-contacts" hidden></nav>
        <div class="lb-launcher-row">
          <div class="lb-label" hidden><span class="lb-dot"></span><span class="lb-label-text"></span><button type="button" data-action="dismiss-label">${ICONS.close}</button></div>
          <button type="button" class="lb-launcher" aria-expanded="false">${ICONS.chat}<span class="lb-badge" hidden></span></button>
        </div>
      </div>`;
    this.body = this.root.querySelector('.lb-body')!;
    this.suggestions = this.root.querySelector('.lb-suggestions')!;
    this.composer = this.root.querySelector('.lb-composer')!;
    this.input = this.composer.querySelector('textarea')!;
    this.sendButton = this.composer.querySelector('.lb-send')!;

    this.root.querySelector('.lb-launcher')!.addEventListener('click', () => this.setOpen(!this.isOpen));
    this.root.querySelector('.lb-label-text')!.addEventListener('click', () => this.setOpen(true));
    this.root.querySelector('[data-action="dismiss-label"]')!.addEventListener('click', () => {
      this.labelDismissed = true;
      if (!this.preview) try { sessionStorage.setItem('lb_label_dismissed', '1'); } catch { /* storage blocked */ }
      this.updateLauncher();
    });
    this.root.querySelector('[data-action="close"]')!.addEventListener('click', () => this.setOpen(false));
    const menu = this.root.querySelector('.lb-menu') as HTMLElement;
    this.root.querySelector('[data-action="menu"]')!.addEventListener('click', () => { menu.hidden = !menu.hidden; });
    this.root.querySelector('[data-action="new-chat"]')!.addEventListener('click', () => { menu.hidden = true; this.resetConversation(); });

    this.composer.addEventListener('submit', event => { event.preventDefault(); this.submitComposer(); });
    this.input.addEventListener('input', () => this.autosize());
    this.input.addEventListener('keydown', event => {
      if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) { event.preventDefault(); this.submitComposer(); }
    });
  }

  private updateLauncher() {
    const launcher = this.root.querySelector('.lb-launcher') as HTMLElement;
    launcher.innerHTML = `${this.isOpen ? ICONS.down : ICONS.chat}<span class="lb-badge"${this.unread ? '' : ' hidden'}>${this.unread}</span>`;
    launcher.setAttribute('aria-label', this.isOpen ? this.t.close : this.t.open);
    launcher.setAttribute('aria-expanded', String(this.isOpen));
    (this.root.querySelector('[data-action="close"]') as HTMLElement).setAttribute('aria-label', this.t.close);
    (this.root.querySelector('[data-action="menu"]') as HTMLElement).setAttribute('aria-label', this.t.menu);
    (this.root.querySelector('[data-action="new-chat"]') as HTMLElement).textContent = this.t.newChat;

    const label = this.root.querySelector('.lb-label') as HTMLElement;
    const text = this.settings.launcherLabel || this.t.launcher;
    (label.querySelector('.lb-label-text') as HTMLElement).textContent = text;
    // In the dashboard preview a closed bubble comes back when its text or switch is edited.
    const labelKey = `${this.settings.showLauncherLabel}|${text}`;
    if (this.preview && labelKey !== this.labelKey) this.labelDismissed = false;
    this.labelKey = labelKey;
    let dismissed = this.labelDismissed;
    if (!this.preview) try { dismissed ||= sessionStorage.getItem('lb_label_dismissed') === '1'; } catch { /* storage blocked */ }
    label.hidden = this.isOpen || !this.settings.showLauncherLabel || dismissed;
  }

  private setOpen(open: boolean) {
    this.isOpen = open;
    this.root.classList.toggle('lb-open', open);
    (this.root.querySelector('.lb-panel') as HTMLElement).setAttribute('aria-hidden', String(!open));
    if (open) {
      this.unread = 0;
      if (!this.openedOnce && !this.preview) {
        this.openedOnce = true;
        fetch(`${this.apiUrl}/api/analytics`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tenantId: this.tenantId, token: this.token, eventType: 'widget_opened' })
        }).catch(() => {});
      }
      this.scrollToBottom(false);
      this.focusInput(220);
    }
    // On phones the chat covers the page: stop the page behind it from scrolling, then restore it.
    const lock = open && this.root.classList.contains('lb-mobile');
    if (lock && this.savedOverflow === null) {
      this.savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    } else if (!lock && this.savedOverflow !== null) {
      document.documentElement.style.overflow = this.savedOverflow;
      this.savedOverflow = null;
    }
    this.updateLauncher();
  }

  // ---------- Session ----------

  private sessionKey() {
    const id = this.token || this.tenantId;
    return this.preview ? `lb_preview_session_${id}` : `lb_session_${id}`;
  }

  private readSession(): Session | null {
    try {
      const raw = localStorage.getItem(this.sessionKey()) || (this.token ? null : localStorage.getItem('lb_session'));
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.chatId && parsed?.leadId ? parsed : null;
    } catch { return null; }
  }

  private writeSession(session: Session | null) {
    try {
      if (session) localStorage.setItem(this.sessionKey(), JSON.stringify(session));
      else localStorage.removeItem(this.sessionKey());
    } catch { /* storage blocked: the chat still works for this page view */ }
  }

  private async restoreSession() {
    const saved = this.preview && !this.previewLive ? null : this.readSession();
    if (!saved) { this.showStart(); return; }
    const history = await this.fetchHistory(saved);
    if (!history) { this.writeSession(null); this.showStart(); return; }
    this.session = saved;
    this.showChat();
    history.messages.forEach(message => this.appendMessage(message, false));
    if (history.status === 'CLOSED') this.markClosed();
    else this.connectSocket();
    this.scrollToBottom(false);
  }

  private async fetchHistory(session: Session): Promise<{ status: string; messages: ChatMessage[] } | null> {
    try {
      const res = await fetch(`${this.apiUrl}/api/widget/chats/${encodeURIComponent(session.chatId)}/messages?leadId=${encodeURIComponent(session.leadId)}`);
      return res.ok ? await res.json() : null;
    } catch { return null; }
  }

  private connectSocket() {
    if (!this.session) return;
    this.socket = new ChatSocketClient(this.tenantId, this.apiUrl, {
      onMessage: message => { this.hideTyping(); this.appendMessage(message, true); },
      onTyping: () => this.showTyping(),
      onError: kind => { this.hideTyping(); this.notice(kind === 'offline' ? this.t.offline : this.t.sendError); },
      onConnect: async () => {
        // Catch up with anything sent while the socket was (re)connecting; rendered ids are skipped.
        const history = this.session && await this.fetchHistory(this.session);
        history?.messages.forEach(message => this.appendMessage(message, true));
      },
      onClosed: () => this.markClosed()
    });
    this.socket.connect(this.session.chatId, this.session.leadId);
  }

  private async startSession(lead: Record<Field, string>): Promise<boolean> {
    try {
      const res = await fetch(`${this.apiUrl}/api/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: this.tenantId, token: this.token, ...lead, sourceDomain: window.location.hostname })
      });
      const data = await res.json();
      const chatId = data.chat?.id || data.chatId;
      const leadId = data.lead?.id || data.leadId;
      if (!res.ok || !chatId || !leadId) throw new Error(data.error || 'start failed');
      this.session = { chatId, leadId };
      this.writeSession(this.session);
      this.connectSocket();
      return true;
    } catch (error) {
      console.error('[LeadBridge] Could not start chat', error);
      return false;
    }
  }

  private resetConversation() {
    this.socket?.disconnect();
    this.socket = null;
    this.session = null;
    this.writeSession(null);
    this.showStart();
  }

  // ---------- Views ----------

  private captureFields(): Field[] {
    const parts = String(this.config.leadCaptureMode ?? 'FULL').split(',').map(part => part.trim()).filter(part => part && part !== 'nopill');
    const mode = parts.join(',');
    if (!mode || mode === 'NONE') return [];
    if (mode === 'FULL') return ['name', 'email', 'phone'];
    if (mode === 'NAME_ONLY') return ['name'];
    return (['name', 'email', 'phone'] as Field[]).filter(field => parts.includes(field));
  }

  private showStart() {
    this.chatClosed = false;
    const fields = this.captureFields();
    if (fields.length) this.showWelcome(fields);
    else this.showChat(); // no form: the conversation is created with the first message
  }

  private showWelcome(fields: Field[]) {
    this.view = 'welcome';
    this.composer.hidden = true;
    this.suggestions.hidden = true;
    const t = this.t;
    const labels: Record<Field, [string, string, string, string]> = {
      name: [t.name, t.namePh, 'text', 'name'],
      email: [t.email, t.emailPh, 'email', 'email'],
      phone: [t.phone, t.phonePh, 'tel', 'tel']
    };
    this.body.innerHTML = `
      <form class="lb-welcome" novalidate>
        <h2>${escapeHtml(this.config.welcomeTitle || (this.lang === 'en' ? 'Hi there 👋' : '¡Hola! 👋'))}</h2>
        <p>${escapeHtml(this.config.welcomeDesc || (this.lang === 'en' ? 'Leave your details and we will help you right away.' : 'Déjanos tus datos y te atendemos al instante.'))}</p>
        ${fields.map(field => `
          <div class="lb-field" data-field="${field}">
            <label for="lb-${field}">${labels[field][0]}</label>
            <input id="lb-${field}" name="${field}" type="${labels[field][2]}" autocomplete="${labels[field][3]}" placeholder="${labels[field][1]}" />
            <span class="lb-error" hidden></span>
          </div>`).join('')}
        <button type="submit" class="lb-primary">${t.start}</button>
        <p class="lb-fineprint">${t.privacy}</p>
      </form>`;
    const form = this.body.querySelector('form')!;
    form.addEventListener('submit', async event => {
      event.preventDefault();
      const values = { name: '', email: '', phone: '' } as Record<Field, string>;
      let valid = true;
      for (const field of fields) {
        const wrapper = form.querySelector(`[data-field="${field}"]`) as HTMLElement;
        const value = (wrapper.querySelector('input') as HTMLInputElement).value.trim();
        const error = !value ? t.required
          : field === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? t.invalidEmail
            : field === 'phone' && value.replace(/\D/g, '').length < 7 ? t.invalidPhone : '';
        const errorEl = wrapper.querySelector('.lb-error') as HTMLElement;
        errorEl.textContent = error; errorEl.hidden = !error;
        if (error) { wrapper.dataset.error = '1'; valid = false; } else delete wrapper.dataset.error;
        values[field] = value;
      }
      if (!valid) { (form.querySelector('[data-error] input') as HTMLInputElement)?.focus(); return; }
      if (this.preview && !this.previewLive) { this.showChat(); return; }
      const button = form.querySelector('.lb-primary') as HTMLButtonElement;
      button.disabled = true; button.textContent = t.starting;
      if (await this.startSession(values)) this.showChat();
      else { button.disabled = false; button.textContent = t.start; this.notice(t.startError); }
    });
  }

  private showChat() {
    this.view = 'chat';
    this.rendered.clear();
    this.pending = [];
    this.lastSender = '';
    this.lastTime = 0;
    this.visitorHasWritten = false;
    this.body.innerHTML = '<div class="lb-messages" role="log" aria-live="polite"></div>';
    this.composer.hidden = false;
    this.input.disabled = false;
    const greeting = this.config.initialMessage || (this.lang === 'en' ? 'Hi! How can we help you today?' : '¡Hola! ¿En qué podemos ayudarte?');
    this.appendMessage({ id: 'greeting', senderType: 'AI', content: greeting, createdAt: new Date().toISOString() }, false);
    this.renderSuggestions();
    this.focusInput(50);
  }

  /**
   * Puts the cursor in the message box, but only while the visitor is using this page: the dashboard
   * preview re-renders on every keystroke of the settings form and must never steal its cursor.
   */
  private focusInput(delay: number) {
    setTimeout(() => {
      if (document.hasFocus() && this.isOpen && this.view === 'chat' && !this.root.classList.contains('lb-mobile')) this.input.focus();
    }, delay);
  }

  private renderSuggestions() {
    const replies = (Array.isArray(this.config.quickReplies) ? this.config.quickReplies : []).filter(Boolean).slice(0, 6);
    this.suggestions.replaceChildren(...replies.map(reply => this.chip(reply, () => this.send(reply))));
    this.suggestions.hidden = this.visitorHasWritten || !replies.length || this.chatClosed;
  }

  private markClosed() {
    this.chatClosed = true;
    this.socket?.disconnect();
    this.socket = null;
    this.composer.hidden = true;
    this.suggestions.hidden = true;
    const wrap = document.createElement('div');
    wrap.className = 'lb-notice';
    wrap.innerHTML = `<p>${this.t.closed}</p>`;
    const restart = document.createElement('button');
    restart.type = 'button'; restart.className = 'lb-primary'; restart.textContent = this.t.newChat;
    restart.addEventListener('click', () => this.resetConversation());
    wrap.appendChild(restart);
    this.messages()?.appendChild(wrap);
    this.scrollToBottom(true);
  }

  // ---------- Messages ----------

  private messages() { return this.body.querySelector('.lb-messages') as HTMLElement | null; }

  private chip(label: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button'; button.className = 'lb-chip'; button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  private notice(text: string) {
    const list = this.messages() || this.body;
    const element = document.createElement('div');
    element.className = 'lb-notice'; element.textContent = text;
    list.appendChild(element);
    this.scrollToBottom(true);
  }

  /** Interactive choices only stay on the latest bot message. */
  private retireChoices() {
    this.body.querySelectorAll('[data-live]').forEach(element => element.remove());
  }

  private appendMessage(message: ChatMessage, live: boolean) {
    const list = this.messages();
    if (!list || this.rendered.has(message.id)) return;
    this.rendered.add(message.id);

    if (message.senderType === 'LEAD') {
      const pending = this.pending.findIndex(item => item.content === message.content);
      if (pending !== -1) {
        const element = this.pending[pending].element;
        element.classList.remove('lb-pending', 'lb-failed');
        if (element.nextElementSibling?.classList.contains('lb-retry')) element.nextElementSibling.remove();
        this.pending.splice(pending, 1);
        return;
      }
      this.visitorHasWritten = true;
      this.suggestions.hidden = true;
    }
    if (message.senderType === 'SYSTEM') { this.notice(message.content); return; }

    const time = new Date(message.createdAt).getTime();
    if (this.lastTime && time - this.lastTime > 15 * 60_000) {
      const stamp = document.createElement('div');
      stamp.className = 'lb-notice'; stamp.textContent = formatTime(message.createdAt, this.lang);
      list.appendChild(stamp);
      this.lastSender = '';
    }
    this.lastTime = time;

    const mine = message.senderType === 'LEAD';
    const parsed = parseMessage(message.content);
    if (!mine && (parsed.buttons.length || parsed.slots.length || parsed.services.length || parsed.extras.length)) this.retireChoices();
    const row = this.row(mine ? 'me' : 'bot');

    const stack = row.querySelector('.lb-stack') as HTMLElement;
    if (parsed.text) {
      const bubble = document.createElement('div');
      bubble.className = 'lb-bubble';
      bubble.innerHTML = renderRichText(parsed.text);
      bubble.title = formatTime(message.createdAt, this.lang);
      stack.appendChild(bubble);
    }
    if (!mine) {
      if (parsed.services.length) stack.appendChild(this.renderServices(parsed.services));
      if (parsed.extras.length) stack.appendChild(this.renderExtras(parsed.extras));
      if (parsed.slots.length) stack.appendChild(this.renderSlots(parsed.slots));
      if (parsed.buttons.length) {
        const chips = document.createElement('div');
        chips.className = 'lb-chips'; chips.dataset.live = '1';
        parsed.buttons.forEach(label => chips.appendChild(this.chip(label, () => this.send(label))));
        stack.appendChild(chips);
      }
      if (parsed.booking) stack.appendChild(this.renderBooking(parsed.booking));
    }
    if (!stack.childElementCount) { row.remove(); return; }
    list.appendChild(row);

    if (live && !mine && !this.isOpen) {
      this.unread += 1;
      this.updateLauncher();
    }
    this.scrollToBottom(live);
  }

  private row(side: 'me' | 'bot'): HTMLElement {
    const row = document.createElement('div');
    row.className = `lb-row lb-${side}${this.lastSender !== side ? ' lb-new-group' : ''}`;
    if (side === 'bot') {
      const mini = document.createElement('div');
      mini.className = 'lb-mini';
      this.renderAvatar(mini);
      // The avatar is shown once per group of consecutive bot messages.
      if (this.lastSender === 'bot') mini.classList.add('lb-hidden');
      row.appendChild(mini);
    }
    const stack = document.createElement('div');
    stack.className = 'lb-stack';
    row.appendChild(stack);
    this.lastSender = side;
    return row;
  }

  private renderServices(services: ServiceOption[]): HTMLElement {
    const list = document.createElement('div');
    list.className = 'lb-services';
    for (const service of services) {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'lb-service';
      button.innerHTML = `<span class="lb-service-info"><strong>${escapeHtml(service.name)}</strong>${service.detail ? `<span>${escapeHtml(service.detail)}</span>` : ''}</span>${service.price ? `<span class="lb-service-price">${escapeHtml(service.price)}</span>` : ''}`;
      button.addEventListener('click', () => this.send(this.t.interested(service.name), { service: service.name }));
      list.appendChild(button);
    }
    return list;
  }

  /** Optional extras: tick any, then one tap adds them all (or skips them). */
  private renderExtras(extras: ServiceOption[]): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'lb-extras'; panel.dataset.live = '1';
    panel.innerHTML = `<div class="lb-slots-title">${ICONS.plus}<span>${escapeHtml(this.t.extrasTitle)}</span></div>`;
    const chosen = new Set<string>();
    const submit = document.createElement('button');
    submit.type = 'button'; submit.className = 'lb-extras-submit';
    const refresh = () => { submit.textContent = chosen.size ? this.t.addExtras(chosen.size) : this.t.noExtras; submit.classList.toggle('lb-active', chosen.size > 0); };
    for (const extra of extras) {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'lb-extra'; button.setAttribute('aria-pressed', 'false');
      button.innerHTML = `<span class="lb-extra-check" aria-hidden="true">${ICONS.tick}</span><span class="lb-service-info"><strong>${escapeHtml(extra.name)}</strong>${extra.detail ? `<span>${escapeHtml(extra.detail)}</span>` : ''}</span>${extra.price ? `<span class="lb-service-price">${escapeHtml(extra.price)}</span>` : ''}`;
      button.addEventListener('click', () => {
        if (chosen.has(extra.name)) chosen.delete(extra.name); else chosen.add(extra.name);
        button.setAttribute('aria-pressed', String(chosen.has(extra.name)));
        refresh();
      });
      panel.appendChild(button);
    }
    submit.addEventListener('click', () => {
      panel.querySelectorAll('button').forEach(item => item.setAttribute('disabled', ''));
      const names = [...chosen];
      this.send(names.length ? this.t.addedExtras(names) : this.t.noExtras, { addOns: names.join('|') });
    });
    refresh();
    panel.appendChild(submit);
    return panel;
  }

  private renderSlots(slots: string[]): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'lb-slots'; panel.dataset.live = '1';
    panel.innerHTML = `<div class="lb-slots-title">${ICONS.clock}<span>${this.t.pickTime}</span></div>`;
    const today = todayIn(this.config.booking?.timezone);
    for (const group of groupSlots(slots)) {
      const day = document.createElement('div');
      day.className = 'lb-slot-day';
      day.innerHTML = `<div class="lb-slot-day-label">${escapeHtml(dayLabel(group.date + 'T00:00', this.lang, today, this.t))}</div>`;
      const grid = document.createElement('div');
      grid.className = 'lb-slot-grid';
      for (const slot of group.slots) {
        const chip = this.chip(timeLabel(slot, this.lang), () => {
          panel.querySelectorAll('.lb-chip').forEach(other => other.setAttribute('disabled', ''));
          chip.classList.add('lb-chosen');
          this.send(this.t.pickedTime(fullSlotLabel(slot, this.lang)), { slot });
        });
        grid.appendChild(chip);
      }
      day.appendChild(grid);
      panel.appendChild(day);
    }
    return panel;
  }

  private renderBooking(card: BookingCard): HTMLElement {
    const t = this.t;
    const element = document.createElement('div');
    element.className = 'lb-booking';
    const rows: [string, string | undefined][] = [
      [t.service, card.service], [t.extras, card.extras], [t.when, card.slotLabel], [t.vehicle, card.vehicle], [t.where, card.address], [t.price, card.price]
    ];
    element.innerHTML = `
      <div class="lb-booking-head">${ICONS.check}<div><strong>${t.confirmed}</strong><span>${t.code} <code>${escapeHtml(card.code)}</code></span></div></div>
      <dl>${rows.filter(([, value]) => value).map(([label, value]) => `<dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value!)}</dd>`).join('')}</dl>
      <a href="${escapeHtml(calendarLink(card, this.config.businessName || this.displayName(), this.config.booking?.timezone))}" target="_blank" rel="noopener">${t.addCalendar}</a>`;
    return element;
  }

  private showTyping() {
    const list = this.messages();
    if (!list || this.root.querySelector('.lb-typing-row')) return;
    const row = this.row('bot');
    row.classList.add('lb-typing-row');
    (row.querySelector('.lb-stack') as HTMLElement).innerHTML = '<div class="lb-bubble lb-typing" aria-label="…"><i></i><i></i><i></i></div>';
    list.appendChild(row);
    this.lastSender = '';
    this.scrollToBottom(true);
    if (this.typingTimer) clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => this.hideTyping(), 60_000);
  }

  private hideTyping() {
    const row = this.root.querySelector('.lb-typing-row');
    if (row) {
      row.remove();
      const last = this.messages()?.lastElementChild;
      this.lastSender = last?.classList.contains('lb-bot') ? 'bot' : last?.classList.contains('lb-me') ? 'me' : '';
    }
    if (this.typingTimer) clearTimeout(this.typingTimer);
    this.typingTimer = null;
  }

  private scrollToBottom(smooth: boolean) {
    requestAnimationFrame(() => this.body.scrollTo({ top: this.body.scrollHeight, behavior: smooth ? 'smooth' : 'auto' }));
  }

  // ---------- Sending ----------

  private autosize() {
    this.input.style.height = 'auto';
    this.input.style.height = `${Math.min(this.input.scrollHeight, 120)}px`;
    this.sendButton.disabled = !this.input.value.trim();
  }

  private submitComposer() {
    const text = this.input.value.trim();
    if (!text) return;
    this.input.value = '';
    this.autosize();
    this.send(text);
  }

  private async send(text: string, meta?: Record<string, string>) {
    if (this.chatClosed) return;
    const list = this.messages();
    if (!list) return;
    this.visitorHasWritten = true;
    this.suggestions.hidden = true;
    this.retireChoices();

    const row = this.row('me');
    const bubble = document.createElement('div');
    bubble.className = 'lb-bubble lb-pending';
    bubble.innerHTML = renderRichText(text);
    row.querySelector('.lb-stack')!.appendChild(bubble);
    list.appendChild(row);
    this.scrollToBottom(true);

    if (this.preview && !this.previewLive) {
      bubble.classList.remove('lb-pending');
      return;
    }
    if (!this.session) {
      // No contact form: the conversation is created with the first message.
      if (!(await this.startSession({ name: '', email: '', phone: '' }))) {
        row.remove();
        this.notice(this.t.startError);
        return;
      }
    }
    const entry: PendingMessage = { content: text, element: bubble, meta };
    this.pending.push(entry);
    this.socket?.sendMessage(text, meta);
    this.watchDelivery(entry);
  }

  /**
   * The server echoes every visitor message. Without the echo, look for it in the history; if it is
   * still missing, show it as not delivered with a retry instead of waiting forever.
   */
  private watchDelivery(entry: PendingMessage) {
    setTimeout(async () => {
      if (!this.pending.includes(entry)) return;
      const history = this.session && await this.fetchHistory(this.session);
      history?.messages.forEach(message => this.appendMessage(message, true));
      if (!this.pending.includes(entry)) return;
      entry.element.classList.remove('lb-pending');
      entry.element.classList.add('lb-failed');
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'lb-retry';
      retry.textContent = this.t.notDelivered;
      retry.addEventListener('click', () => {
        retry.remove();
        entry.element.classList.remove('lb-failed');
        entry.element.classList.add('lb-pending');
        this.socket?.sendMessage(entry.content, entry.meta);
        this.watchDelivery(entry);
      });
      entry.element.after(retry);
    }, DELIVERY_TIMEOUT_MS);
  }
}

(window as any).LeadBridge = {
  init: (tenantId: string) => new LeadBridgeWidget(tenantId)
};

// Auto-initialize if data-token is present on the script tag
const currentScript = (document.currentScript as HTMLScriptElement) || (document.querySelector('script[data-token]') as HTMLScriptElement);
if (currentScript && currentScript.getAttribute('data-token')) {
  const token = currentScript.getAttribute('data-token') as string;
  const isPreviewMode = currentScript.getAttribute('data-preview-mode') === 'true';
  let apiUrl = 'http://localhost:3001';
  try {
    const src = currentScript.getAttribute('src');
    if (src) apiUrl = new URL(src, window.location.href).origin;
  } catch {
    console.warn('[LeadBridge] Could not determine API URL from script source, falling back to localhost.');
  }
  new LeadBridgeWidget(token, true, apiUrl, isPreviewMode);
}
